import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import FilterListIcon from '@mui/icons-material/FilterList';
import { alpha, Button, Grid, Stack, styled } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductCatalogBrowseDrawer from '../Browse/ProductCatalogBrowseDrawer';
import ProductCatalogActiveFilters from '../Filters/ProductCatalogActiveFilters';
import ProductCatalogFilterDrawer from '../Filters/ProductCatalogFilterDrawer';
import ProductCatalogSearchControl from './ProductCatalogSearchControl';
import type { ProductSearchOption } from '../hooks/useProductCatalogSearchOptions';
import useProductCatalogActions from '../hooks/useProductCatalogActions';
import useProductCatalogFiltering from '../hooks/useProductCatalogFiltering';
import useProductCatalogProducts from '../hooks/useProductCatalogProducts';

const CatalogToolbar = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.25),
}));

const DesktopCatalogToolbar = styled(CatalogToolbar)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileControlSurface = styled(Stack)(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.down('sm')]: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    display: 'flex',
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    padding: theme.spacing(1.25, 2, 1.5),
  },
}));

const ToolbarActions = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  width: '100%',

  [theme.breakpoints.down('sm')]: {
    alignItems: 'stretch',
    width: '100%',

    '& .MuiButton-root': {
      justifyContent: 'center',
    },
  },
}));

const MobileActionRow = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
}));

const CatalogActionButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'actionTone' && prop !== 'iconOnlyOnMobile',
})<{
  actionTone?: 'favorite'
  iconOnlyOnMobile?: boolean
  selected?: boolean
}>(({ actionTone, iconOnlyOnMobile, selected, theme }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',

  ...(actionTone === 'favorite' ? {
    backgroundColor: selected === true ? alpha(theme.palette.accent.main, 0.1) : undefined,
    borderColor: selected === true ? theme.palette.accent.main : undefined,
    color: selected === true ? theme.palette.accent.main : theme.palette.text.primary,

    '&:hover': {
      backgroundColor: selected === true ? alpha(theme.palette.accent.main, 0.16) : undefined,
      borderColor: selected === true ? alpha(theme.palette.accent.main, 0.54) : undefined,
    },
  } : {}),

  [theme.breakpoints.down('md')]: {
    flex: iconOnlyOnMobile === true ? '0 0 40px' : '0 0 0',
    minHeight: 40,
    minWidth: iconOnlyOnMobile === true ? 40 : 'auto',
    lineHeight: 1,
    paddingLeft: iconOnlyOnMobile === true ? 0 : null,
    paddingRight: iconOnlyOnMobile === true ? 0 : null,

    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: iconOnlyOnMobile === true ? 0 : theme.spacing(0.5),
    },

    ...(iconOnlyOnMobile === true ? {
      '& .catalog-action-label': {
        display: 'none',
      },
    } : {}),
  },
}));

const ProductCountText = styled('span')(({ theme }) => ({
  alignItems: 'center',
  borderLeft: '1px solid ' + alpha(theme.palette.common.black, 0.12),
  color: theme.palette.text.secondary,
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: 1.35,
  marginLeft: theme.spacing(0.5),
  paddingLeft: theme.spacing(1.5),
  whiteSpace: 'nowrap',
  width: 'fit-content',

  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

interface ProductCatalogToolbarProps {
  includeBrowse?: boolean
  includeBrowseDrawer?: boolean
  includeCategorySearchOptions?: boolean
  includeFavorites?: boolean
  onBrowseOpen?: () => void
  onCategorySearchOptionSelect?: (option: ProductSearchOption) => void
  productSearchOptionFilter?: (option: ProductSearchOption) => boolean
  resetProductSearchToAllProducts?: boolean
}

export default function ProductCatalogToolbar({
  includeBrowse = true,
  includeBrowseDrawer = true,
  includeCategorySearchOptions = true,
  includeFavorites = true,
  onBrowseOpen: onBrowseOpenProp,
  onCategorySearchOptionSelect,
  productSearchOptionFilter,
  resetProductSearchToAllProducts = true,
}: ProductCatalogToolbarProps) {
  const [t] = useTranslation('products');
  const [browseOpened, setBrowseOpened] = useState(false);
  const [filtersOpened, setFiltersOpened] = useState(false);
  const {
    toggleFavorites,
  } = useProductCatalogActions();
  const {
    visibleProductCount,
  } = useProductCatalogProducts();
  const {
    activeAttributeFilterCount,
    favoritesSelected,
  } = useProductCatalogFiltering();
  const filterButtonLabel = activeAttributeFilterCount > 0
    ? t('Filters') + ' ' + activeAttributeFilterCount
    : t('Filters');

  const onBrowseOpen = useCallback(() => {
    if (onBrowseOpenProp !== undefined) {
      onBrowseOpenProp();
      return;
    }

    setBrowseOpened(true);
  }, [onBrowseOpenProp])
  const onBrowseClose = useCallback(() => setBrowseOpened(false), [])
  const onFiltersOpen = useCallback(() => setFiltersOpened(true), [])
  const onFiltersClose = useCallback(() => setFiltersOpened(false), [])

  const renderCatalogActionButtons = (showBrowseButton: boolean) => <>
    {includeBrowse && showBrowseButton ? <CatalogActionButton
      color="secondary"
      size="medium"
      startIcon={<CategoryRoundedIcon />}
      variant="outlined"
      onClick={onBrowseOpen}
    >{t('Categories')}</CatalogActionButton> : null}
    <CatalogActionButton
      color="primary"
      size="medium"
      startIcon={<FilterListIcon />}
      variant="contained"
      onClick={onFiltersOpen}
    >{filterButtonLabel}</CatalogActionButton>
    {includeFavorites ? <CatalogActionButton
      actionTone="favorite"
      aria-label={t('My favorites')}
      aria-pressed={favoritesSelected}
      color="secondary"
      iconOnlyOnMobile
      selected={favoritesSelected}
      size="medium"
      startIcon={favoritesSelected ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
      variant="outlined"
      onClick={toggleFavorites}
    >
      <span className="catalog-action-label">{t('My favorites')}</span>
    </CatalogActionButton> : null}
  </>;

  return <>
    <Grid item xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
      <DesktopCatalogToolbar>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={1}
          alignItems={{
            xs: 'stretch',
            md: 'center',
          }}
          justifyContent="space-between"
        >
          <ToolbarActions
            direction={{
              xs: 'column',
              sm: 'row',
            }}
          >
            <ProductCatalogSearchControl
              includeCategoryOptions={includeCategorySearchOptions}
              onCategoryOptionSelect={onCategorySearchOptionSelect}
              optionFilter={productSearchOptionFilter}
              resetSearchToAllProducts={resetProductSearchToAllProducts}
            />
            {renderCatalogActionButtons(false)}
          </ToolbarActions>
          <ProductCountText>
            {t('{{count}} products', { count: visibleProductCount })}
          </ProductCountText>
        </Stack>
        <ProductCatalogActiveFilters />
      </DesktopCatalogToolbar>
    </Grid>
    <Grid item xs={12} sx={{ display: { xs: 'block', sm: 'none' } }}>
      <MobileControlSurface spacing={1.25}>
        <ProductCatalogSearchControl
          includeCategoryOptions={includeCategorySearchOptions}
          onCategoryOptionSelect={onCategorySearchOptionSelect}
          optionFilter={productSearchOptionFilter}
          resetSearchToAllProducts={resetProductSearchToAllProducts}
        />
        <MobileActionRow>
          {renderCatalogActionButtons(true)}
        </MobileActionRow>
        <ProductCatalogActiveFilters />
      </MobileControlSurface>
    </Grid>
    {includeBrowse && includeBrowseDrawer ? <ProductCatalogBrowseDrawer open={browseOpened} onClose={onBrowseClose} /> : null}
    <ProductCatalogFilterDrawer open={filtersOpened} onClose={onFiltersClose} />
  </>;
}
